<template>
	<ListView
		:items="groupedClips"
		:swipable="isMobileWebApp"
		:swipe-left-options="{ icon: 'network-communication', action: handleShare }"
		:swipe-right-options="isViewModeOrFullPageMode ? {} : { icon: 'bin', action: handleDelete }"
	>
		<template #no-results>
			<NoResults
				:title="emptyMsg.title"
				:description="emptyMsg.description"
			/>
		</template>
		<template #item-row="{ item }">
			<RunClipListTile
				v-if="item.isRun"
				:clip="item.tag"
				:run-name="runTagNames[item.tag.id]"
				:subtags-count="item.subtagsCount"
				:indented="item.subtagsCount > 0"
				:is-playing="item.tag.id === playingClipId"
				@play="handlePlay"
				@edit="handleEdit"
				@share="handleShare"
				@delete="handleDelete"
			/>
			<ClipListTile
				v-else
				:clip="item.tag"
				:rounded-leading="true"
				:indented="item.isSubtag && !item.isLastSubtag"
				:hide-leading="item.isSubtag"
				:is-playing="item.tag.id === playingClipId"
				@play="handlePlay"
				@edit="handleEdit"
				@share="handleShare"
				@delete="handleDelete"
			>
				<template #leading-placeholder>
					<Icon
						:icon-name="`tag-${item.tag.name}`"
					/>
				</template>
				<template
					v-if="item.isSubtag"
					#subtitle
				>
					{{ item.tag.formattedTimestamp }}
				</template>
			</ClipListTile>
		</template>
	</ListView>
</template>

<script>
import ClipListTile from '@components/clip-list/ClipListTile';
import ListView from '@components/lists/ListView';
import athleteTagsHelpers from '@videotools/js/mixins/athleteTagsHelpers';
import RunClipListTile from '@components/clip-list/RunClipListTile';
import NoResults from '@videotools/js/one/components/TagList/NoResults';
import { mapGetters } from 'vuex';

export default {
	name:       'AthletesClipList',
	components: { NoResults, RunClipListTile, ListView, ClipListTile },
	mixins:     [athleteTagsHelpers],
	props:      {
		clips: {
			type: Array,
		},
		emptyMsg: {
			type: Object,
		},
		playingClipId: {
			type: Number,
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp:           'app/isMobileWebApp',
			isViewModeOrFullPageMode: 'app/isViewModeOrFullPageMode',
		}),
		groupedClips() {
			const runTags = this.getAthleteRunTags(this.clips);
			const runSubtags = {};
			const allSubtags = [];
			const ret = [];

			// get each run tag's subtags
			for (const runTag of runTags) {
				const subtags = this.getAthleteRunSubtags(runTag, this.clips);
				runSubtags[runTag.id] = this.getAthleteRunSubtags(runTag, this.clips);
				allSubtags.push(...subtags);
			}

			// re-arrange the tags to always so that after each run tag, its subtags will follow
			const topLevelTags = this.clips.filter(evTag => !allSubtags.includes(evTag));
			for (const evTag of topLevelTags) {
				const isRun = runTags.includes(evTag);
				ret.push({
					tag:          evTag,
					isRun,
					isSubtag:     false,
					subtagsCount: runSubtags[evTag.id]?.length || 0,
				});

				if (isRun) {
					// add the subtags right after
					const subtags = runSubtags[evTag.id];
					ret.push(...subtags.map((subtag, idx) => {
						return {
							tag:          subtag,
							isRun:        false,
							isSubtag:     true,
							isLastSubtag: idx === subtags.length - 1,
							subtagsCount: 0,
						};
					}));
				}
			}

			return ret;
		},
		runTagNames() {
			return this.generateAllAthleteRunTagNames(this.clips);
		},
	},
	methods: {
		handleShare(clip) {
			this.$emit('share', clip);
		},
		handleDelete(clip) {
			this.$emit('delete', clip);
		},
		handlePlay(clip) {
			this.$emit('play', clip);
		},
		handleEdit(clip) {
			this.$emit('edit', clip);
		},
	},
};
</script>

<style lang="scss" scoped>

</style>
